import {
	DynamicConfigProvider,
	NotAuthorizedPage,
	NotFound,
	Protected,
	getDefaultRemoteConfigFetcher,
} from "@dgs/core";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { FC } from "react";
import { Navigate, Route, RouterProvider, Routes, createBrowserRouter } from "react-router";
import { AppProviders } from "./AppProviders";
import { AppPasswordResetSuccessPage, JarvisAuthPages } from "./auth";
import { BlockPage } from "./blocks/BlocksPage";
import { useFrontendConfig } from "./config/useFrontendConfig";
import { ContactUpdateInquiriesPage } from "./contactUpdateInquiries/ContactUpdateInquiriesPage";
import { Content } from "./Content";
import { EventsPage } from "./events/EventsPage";
import { FeedbackPreviewPage } from "./feedbacks/FeedbackPreviewPage";
import { ProfilePage } from "./profile/ProfilePage";
import { JarvisRedirectPage } from "./redirect/JarvisRedirectPage";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: { staleTime: 100000, retry: false },
	},
});

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const CustomRedirect = () => {
	const { startPageBehaviour, menuItem } = useFrontendConfig();

	if (startPageBehaviour === "eventCalendar") {
		return <Navigate to="/events" replace={true} />;
	} else if (startPageBehaviour === "menuItem") {
		if (menuItem?.type === "PAGE") {
			return <Navigate to={`/blocks/${menuItem.block.slug}`} replace={true} />;
		}
		if (menuItem?.type === "INTERNAL") {
			return <Navigate to={menuItem.url} replace={true} />;
		}
	}

	return <Navigate to="/events" replace={true} />;
};

const AppRoutes: FC = () => {
	return (
		<QueryClientProvider client={queryClient}>
			<DynamicConfigProvider remoteConfigQueryFn={getDefaultRemoteConfigFetcher()}>
				<AppProviders>
					<SentryRoutes>
						<Route path="/auth/">
							<Route path="*" index element={<JarvisAuthPages />} />
						</Route>
						<Route path="/r/:slug" element={<JarvisRedirectPage />} />
						<Route
							path="/"
							element={
								<Protected redirectBack={true}>
									<Content />
								</Protected>
							}
						>
							<Route path="/app-success" element={<AppPasswordResetSuccessPage />} />
							<Route index element={<CustomRedirect />} />
							<Route path="events">
								<Route path="*" index element={<EventsPage />} />
							</Route>
							<Route path="profile">
								<Route path="*" index element={<ProfilePage />} />
							</Route>
							<Route path="blocks/:slug" element={<BlockPage />} />
							<Route path="feedbacks/:feedbackId/preview" element={<FeedbackPreviewPage />} />
						</Route>
						<Route path="not-authorized" element={<NotAuthorizedPage title="JARVIS" redirect={-1} />} />
						<Route path="contact-update-campaign/*" element={<ContactUpdateInquiriesPage />} />
						<Route path="*" element={<NotFound />} />
					</SentryRoutes>
				</AppProviders>
			</DynamicConfigProvider>
		</QueryClientProvider>
	);
};

const router = createBrowserRouter([
	{
		path: "*",
		Component: AppRoutes,
	},
]);

export const App: FC = () => {
	return <RouterProvider router={router} />;
};
